import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { GoogleReviewsSection } from "@/components/GoogleReviews";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarDays, MapPin, Sparkles, HeartPulse, Users, CheckCircle2, Flower2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1747238415033-b74eec07eb59?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FAQS = [
  {
    q: { es: "¿Dónde hacer Pilates en Coronado?", en: "Where to do Pilates in Coronado?", fr: "Où faire du Pilates à Coronado ?" },
    a: { es: "En Frenchies Pilates, en el corazón de Coronado (Panamá Oeste): Pilates Reformer, clases grupales, semi-privadas y privadas para todos los niveles.",
         en: "At Frenchies Pilates, in the heart of Coronado (Panamá Oeste): Pilates Reformer, group, semi-private and private classes for all levels.",
         fr: "Chez Frenchies Pilates, au cœur de Coronado (Panamá Oeste) : Pilates Reformer, cours collectifs, semi-privés et privés pour tous les niveaux." },
  },
  {
    q: { es: "¿Qué es el Pilates Reformer?", en: "What is Pilates Reformer?", fr: "Qu'est-ce que le Pilates Reformer ?" },
    a: { es: "Es un método de Pilates sobre una máquina con resistencia que mejora la postura, la fuerza profunda, la flexibilidad y la energía, con bajo impacto en las articulaciones.",
         en: "It's a Pilates method on a resistance machine that improves posture, deep strength, flexibility and energy, with low impact on the joints.",
         fr: "C'est une méthode de Pilates sur une machine à résistance qui améliore la posture, la force profonde, la souplesse et l'énergie, avec un faible impact sur les articulations." },
  },
  {
    q: { es: "¿Cómo reservar una clase de Pilates en Coronado?", en: "How to book a Pilates class in Coronado?", fr: "Comment réserver un cours de Pilates à Coronado ?" },
    a: { es: "Reserva en línea en segundos desde nuestro horario y paga de forma segura. Elige tu clase de Pilates y asegura tu lugar.",
         en: "Book online in seconds from our schedule and pay securely. Pick your Pilates class and secure your spot.",
         fr: "Réserve en ligne en quelques secondes depuis notre planning et paie en toute sécurité. Choisis ton cours de Pilates et garde ta place." },
  },
];

export default function PilatesCoronado() {
  const { tl } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "pilates-coronado-ldjson";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q.es,
        "acceptedAnswer": { "@type": "Answer", "text": f.a.es },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById("pilates-coronado-ldjson")?.remove(); };
  }, []);

  const PERKS = [
    { icon: Sparkles, t: { es: "Pilates Reformer", en: "Pilates Reformer", fr: "Pilates Reformer" } },
    { icon: Users, t: { es: "Clases grupales & semi-privadas", en: "Group & semi-private classes", fr: "Cours collectifs & semi-privés" } },
    { icon: HeartPulse, t: { es: "Clases privadas & rehabilitación", en: "Private classes & rehab", fr: "Cours privés & rééducation" } },
    { icon: Flower2, t: { es: "Postura, fuerza profunda & energía", en: "Posture, deep strength & energy", fr: "Posture, force profonde & énergie" } },
  ];

  return (
    <PublicLayout>
      <Seo
        title="Pilates à Coronado — Frenchies Pilates | Reformer & clases privadas"
        description="Le studio de Pilates de référence à Coronado, Panamá : Pilates Reformer, cours collectifs, semi-privés et privés. Améliore ta posture et ton énergie. Réserve en ligne."
        path="/pilates-coronado"
        keywords="Pilates Coronado, Pilates Reformer Coronado, clases de Pilates Coronado, studio Pilates Coronado, Pilates Panamá Oeste, Sport Coronado, Fitness Coronado"
      />

      {/* HERO */}
      <section className="relative flex min-h-[70vh] w-full items-center overflow-hidden bg-black">
        <img src={HERO_IMG} alt="Pilates à Coronado — Frenchies Pilates" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 py-20 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold">
            <MapPin className="h-4 w-4" /> Coronado · Panamá Oeste
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {tl({ es: "Pilates en Coronado", en: "Pilates in Coronado", fr: "Pilates à Coronado" })}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-5 max-w-xl text-base text-white/85 md:text-lg">
            {tl({ es: "Pilates Reformer y clases privadas en el corazón de Coronado. Mejora tu postura, fuerza y energía. Reserva tu clase en línea.",
                  en: "Pilates Reformer and private classes in the heart of Coronado. Improve your posture, strength and energy. Book your class online.",
                  fr: "Pilates Reformer et cours privés au cœur de Coronado. Améliore ta posture, ta force et ton énergie. Réserve ton cours en ligne." })}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34 }}
            className="mt-8 flex flex-wrap items-center gap-3">
            <motion.button data-testid="pilates-coronado-book-btn" onClick={() => navigate("/schedule")}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-[0_0_34px_hsl(var(--primary)/0.5)]">
              <CalendarDays className="h-5 w-5" />
              {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance" })}
              <ArrowRight className="h-4 w-4" />
            </motion.button>
            <Button size="lg" variant="outline" data-testid="pilates-coronado-pricing-btn" onClick={() => navigate("/pricing")}
              className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20">
              {tl({ es: "Ver precios", en: "See pricing", fr: "Voir les tarifs" })}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "El studio de Pilates de referencia en Coronado", en: "The go-to Pilates studio in Coronado", fr: "Le studio de Pilates de référence à Coronado" })}
        </h2>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          {tl({
            es: "Frenchies Pilates es el studio de Pilates premium de Coronado, Panamá. Si buscas Pilates en Coronado, clases de Pilates Reformer en Coronado o un studio de Pilates cerca de ti, aquí encuentras clases grupales, semi-privadas y privadas para todos los niveles. Nuestras coaches te acompañan para mejorar tu postura, tu fuerza profunda y tu energía, en pleno Coronado (Panamá Oeste).",
            en: "Frenchies Pilates is the premium Pilates studio in Coronado, Panama. If you're looking for Pilates in Coronado, Pilates Reformer classes in Coronado or a Pilates studio near you, you'll find group, semi-private and private classes for all levels. Our coaches help you improve your posture, deep strength and energy, right in Coronado (Panamá Oeste).",
            fr: "Frenchies Pilates est le studio de Pilates premium de Coronado, Panama. Si tu cherches du Pilates à Coronado, des cours de Pilates Reformer à Coronado ou un studio de Pilates près de chez toi, tu trouveras des cours collectifs, semi-privés et privés pour tous les niveaux. Nos coachs t'accompagnent pour améliorer ta posture, ta force profonde et ton énergie, en plein cœur de Coronado (Panamá Oeste)." })}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PERKS.map(({ icon: Icon, t }, i) => (
            <div key={i} data-testid={`pilates-perk-${i}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
              <Icon className="h-6 w-6 shrink-0 text-primary" />
              <span className="font-medium">{tl(t)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">
            {tl({ es: "¿Por qué hacer Pilates en Frenchies Coronado?", en: "Why do Pilates at Frenchies Coronado?", fr: "Pourquoi faire du Pilates chez Frenchies Coronado ?" })}
          </h2>
          <ul className="space-y-3">
            {[
              { es: "Máquinas Reformer de calidad y grupos reducidos", en: "Quality Reformer machines and small groups", fr: "Machines Reformer de qualité et petits groupes" },
              { es: "Instructoras certificadas, enfoque postura y salud", en: "Certified instructors, posture and health focus", fr: "Instructrices certifiées, approche posture et santé" },
              { es: "Clases privadas y semi-privadas a tu ritmo", en: "Private and semi-private classes at your pace", fr: "Cours privés et semi-privés à ton rythme" },
              { es: "Reserva y pago 100% en línea, ubicación céntrica en Coronado", en: "100% online booking and payment, central Coronado location", fr: "Réservation et paiement 100% en ligne, emplacement central à Coronado" },
            ].map((item, i) => (
              <li key={i} data-testid={`pilates-why-${i}`} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-muted-foreground">{tl(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "Preguntas frecuentes", en: "Frequently asked questions", fr: "Questions fréquentes" })}
        </h2>
        <div className="space-y-5">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`pilates-faq-${i}`} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold">{tl(f.q)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tl(f.a)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <GoogleReviewsSection compact />

      <section className="mx-auto max-w-5xl px-5 py-16 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
          {tl({ es: "Empieza tu Pilates en Coronado hoy", en: "Start your Pilates in Coronado today", fr: "Commence ton Pilates à Coronado dès aujourd'hui" })}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          {tl({ es: "Reserva tu primera clase de Pilates Reformer en Coronado en menos de un minuto.", en: "Book your first Pilates Reformer class in Coronado in under a minute.", fr: "Réserve ton premier cours de Pilates Reformer à Coronado en moins d'une minute." })}
        </p>
        <Button size="lg" data-testid="pilates-coronado-cta-btn" onClick={() => navigate("/schedule")} className="mt-7 rounded-full">
          {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance" })} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>
    </PublicLayout>
  );
}
